'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEventWorkspace } from '@/components/event-workspace/event-workspace-context';
import { useEventWizardStore } from '@/stores/event-wizard-store';
import { WIZARD_STEPS, isWizardBlockedStatus } from '@/components/event-wizard/wizard-utils';
import { useOrganizerExperience } from '@/hooks/useOrganizerExperience';
import type { OrganizerExperienceNavGroup } from '@/lib/api';
import { friendlyFormatLabel } from '@/lib/event-format-labels';
import { useEventChatUnread } from '@/hooks/useEventChatUnread';
import { useOrganizerInboxUnread } from '@/hooks/useOrganizerInboxUnread';
import { useEntitlements } from '@/hooks/useEntitlements';
import { StaticDynamicLink } from '@/lib/static-export-nav';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';
import { readEventOrigin } from '@/lib/workspace-context';
import {
  LayoutDashboard,
  Users,
  Mail,
  Workflow,
  Award,
  Settings,
  BarChart3,
  ChevronRight,
  ShieldCheck,
  Check,
  UserCheck,
  ClipboardList,
  MapPin,
  FileText,
  Vote,
  BadgeCheck,
  Landmark,
  Search,
  Eye,
  MessageSquare,
  Wallet,
  UsersRound,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { subscriptionApi } from '@/lib/api';
import { triggerPlanUpgrade } from '@/components/plans/UpgradeModal';
export const PLAN_RANK: Record<string, number> = {
  free: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  custom: 4,
  enterprise: 4,
};

const ICON_MAP: Record<string, LucideIcon> = {
  'layout-dashboard': LayoutDashboard,
  users: Users,
  mail: Mail,
  workflow: Workflow,
  award: Award,
  settings: Settings,
  'bar-chart': BarChart3,
  'user-check': UserCheck,
  'clipboard-list': ClipboardList,
  'map-pin': MapPin,
  'file-text': FileText,
  vote: Vote,
  badge: BadgeCheck,
  landmark: Landmark,
  search: Search,
  eye: Eye,
  'message-square': MessageSquare,
  wallet: Wallet,
  'users-round': UsersRound,
};

const FALLBACK_SECTIONS = [
  { slug: 'overview', label: 'Overview', icon: LayoutDashboard },
  { slug: 'attendees', label: 'Attendees', icon: Users },
  { slug: 'activities', label: 'Activities', icon: Workflow },
  { slug: 'communications', label: 'Communications', icon: Mail },
  { slug: 'attendance', label: 'Attendance', icon: UserCheck },
  { slug: 'live', label: 'Live', icon: BarChart3 },
  { slug: 'analytics', label: 'Analytics', icon: BarChart3 },
  { slug: 'certificates', label: 'Certificates', icon: Award },
  { slug: 'reports', label: 'Reports', icon: FileText },
  // { slug: 'visibility', label: 'Visibility & sales', icon: Eye },
  // { slug: 'evaluation', label: 'Evaluation Sheet', icon: ClipboardList },

  { slug: 'settings', label: 'Settings', icon: Settings },
];

/** Collapse overlapping messaging/comms routes into one sidebar entry. */
const NAV_SLUG_ALIASES: Record<string, string> = {
  announcements: 'communications',
  communication: 'communications',
  messaging: 'communications',
  messages: 'communications',
  inbox: 'communications',
  chat: 'communications',
  'communications/broadcasts': 'communications',
};

const SLUG_FEATURE_MAP: Record<string, { feature: string; aliases?: string[]; requiredPlan: string }> = {
  communications: { feature: 'communication', aliases: ['communication.external.broadcast', 'communications'], requiredPlan: 'bronze' },
  attendance: { feature: 'attendance', aliases: ['attendance.management', 'attendance.check_in', 'attendance.qr'], requiredPlan: 'bronze' },
  forms: { feature: 'forms', aliases: ['forms.custom_builder', 'forms.registration'], requiredPlan: 'bronze' },
  'team-management': { feature: 'team_registration', aliases: ['participant.team_registration', 'team-management'], requiredPlan: 'silver' },
  analytics: { feature: 'analytics', aliases: ['analytics.basic', 'analytics.advanced'], requiredPlan: 'silver' },
  reports: { feature: 'reports', aliases: ['analytics.export_reports', 'analytics.export'], requiredPlan: 'silver' },
  certificates: { feature: 'certificates', aliases: ['certificates.bulk_generation', 'certificates.issue'], requiredPlan: 'silver' },
  evaluation: { feature: 'jury', aliases: ['jury.management', 'jury.panel_scoring'], requiredPlan: 'silver' },
  'evaluation-dashboard': { feature: 'jury', aliases: ['jury.management', 'jury.panel_scoring', 'evaluation'], requiredPlan: 'silver' },
  results: { feature: 'jury', aliases: ['jury.management', 'jury.panel_scoring', 'results', 'evaluation'], requiredPlan: 'silver' },
};

function canonicalizeNavSlug(slug: string): string {
  const cleaned = String(slug || '')
    .replace(/^\/+/, '')
    .trim();
  return NAV_SLUG_ALIASES[cleaned] ?? cleaned;
}

function dedupeNavGroupItems<T extends { id?: string; route_slug?: string; module_key?: string }>(
  items: T[],
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const slug = canonicalizeNavSlug(item.route_slug ?? item.module_key ?? item.id ?? '');
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push(item);
  }
  return out;
}

function dedupeNavSections<T extends { slug: string; label: string }>(sections: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const section of sections) {
    const slug = canonicalizeNavSlug(section.slug);
    if (seen.has(slug)) continue;
    seen.add(slug);
    out.push({
      ...section,
      slug,
      label: slug === 'communications' ? 'Communications' : section.label,
    });
  }
  return out;
}


/** Nested module groups still hide duplicate discovery/visibility entries; top-level nav injects it separately. */
const HIDDEN_WORKSPACE_MODULES = new Set(['discovery', 'visibility']);

const CREATION_STEP_LABELS = [
  'Basics',
  'Dates & Location',
  'Registration Settings',
  'Registration Form',
  'Tickets',
  'Advanced',
  'Review',
];

function EventWorkspaceSidebarComponent() {
  const pathname = usePathname();
  const { eventId, event, loading } = useEventWorkspace();
  const { experience, loading: experienceLoading } = useOrganizerExperience(eventId);
  const { stepIndex, setStepIndex, draftTitle } = useEventWizardStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNewEventRoute = pathname === '/dashboard/events/new' || Boolean(pathname?.startsWith('/dashboard/events/new/'));
  const hasEventId = Boolean(eventId);
  const base = hasEventId ? `/dashboard/events/${eventId}` : '/dashboard/events/new';
  const editHref = hasEventId ? `${base}/edit` : '/dashboard/events/new';

  const eventStatus = typeof event?.status === 'string' ? event.status : undefined;
  const isCreationMode =
    (isNewEventRoute || Boolean(pathname?.includes('/edit'))) && !isWizardBlockedStatus(eventStatus);

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(`sidebar-scroll-pos-${eventId}`);
    if (savedScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = parseInt(savedScroll, 10);
    }
  }, [eventId]);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem(`sidebar-scroll-pos-${eventId}`, scrollContainerRef.current.scrollTop.toString());
    }
  };

  const navSections = useMemo(() => {
    let sections: Array<{ slug: string; label: string; icon: LucideIcon }>;
    if (experience?.navigation?.sections?.length) {
      sections = experience.navigation.sections.map((section) => ({
        slug: canonicalizeNavSlug(section.route_slug),
        label: section.label,
        icon: ICON_MAP[section.icon ?? ''] ?? LayoutDashboard,
      }));
    } else {
      sections = [...FALLBACK_SECTIONS];
    }

        sections = dedupeNavSections(sections);

    if (!sections.some((section) => section.slug === 'activities')) {
      sections = [
        ...sections,
        { slug: 'activities', label: 'Activities', icon: Workflow },
      ];
    }

    const ensureAfter = (
      slug: string,
      label: string,
      icon: LucideIcon,
      afterSlug: string,
    ) => {
      const canonical = canonicalizeNavSlug(slug);
      if (sections.some((section) => section.slug === canonical)) return;
      const item = { slug: canonical, label, icon };
      const afterIndex = sections.findIndex((section) => section.slug === afterSlug);
      if (afterIndex >= 0) {
        sections = [
          ...sections.slice(0, afterIndex + 1),
          item,
          ...sections.slice(afterIndex + 1),
        ];
      } else {
        sections = [...sections, item];
      }
    };

    // Drop Payments & Taxes from event workspace nav (finance lives in Tickets wizard).
    // Messaging lives only under Communications (aliases already folded above).
    sections = sections
      .filter(
        (section) =>
          section.slug !== 'payments-taxes' &&
          section.slug !== 'email-campaigning' &&
          section.slug !== 'program' &&
          section.label.toLowerCase() !== 'program' &&
          section.slug !== 'attendees' &&
          section.slug !== 'certificates' &&
          section.slug !== 'bank-details' &&
          section.slug !== 'visibility' &&
          section.label.toLowerCase() !== 'visibility & sales' &&
          section.slug !== 'evaluation' &&
          section.label.toLowerCase() !== 'evaluation settings' &&
          !section.slug.includes('discovery') &&
          !section.label.toLowerCase().includes('discovery')
      )
      .map((section) => {
        if (section.slug === 'communications') {
          return { ...section, label: 'Communications', icon: Mail };
        }
        return section;
      });

    sections = dedupeNavSections(sections);

    ensureAfter('participants', 'Participants', Users, 'overview');
    ensureAfter('communications', 'Communications', Mail, 'participants');
    ensureAfter('team-management', 'Team Access & Roles', ShieldCheck, 'communications');
    ensureAfter(
      'evaluation-dashboard',
      'Evaluation',
      ClipboardList,
      'team-management',
    );
    ensureAfter('reports', 'Reports', FileText, 'evaluation-dashboard');

    return dedupeNavSections(sections);
  }, [experience]);

  const navGroupsByParent = useMemo(() => {
    const map = new Map<string, OrganizerExperienceNavGroup[]>();
    for (const group of experience?.navigation?.groups ?? []) {
      const key = canonicalizeNavSlug(group.parent_section);
      // Messaging sub-items are injected below — skip API duplicates under communications.
      if (key === 'communications') continue;
      if (!map.has(key)) map.set(key, []);
      const filtered = {
        ...group,
        parent_section: key,
        items: dedupeNavGroupItems(
          (group.items ?? []).filter((item) => {
            const slug = canonicalizeNavSlug(item.module_key ?? item.route_slug ?? '');
            return (
              !HIDDEN_WORKSPACE_MODULES.has(item.module_key ?? item.route_slug ?? '') &&
              !HIDDEN_WORKSPACE_MODULES.has(slug) &&
              slug !== 'communications' &&
              !slug.includes('discovery')
            );
          }),
        ),
      };
      if ((filtered.items ?? []).length > 0) {
        map.get(key)!.push(filtered);
      }
    }

    return map;
  }, [experience]);

  const formatLabel = friendlyFormatLabel(experience?.event?.format_label, 'Event');
  const isMunWorkspace = experience?.workspace_mode === 'mun';
  const trimmedDraftTitle = draftTitle.trim();
  const savedEventTitle =
    typeof event?.title === 'string' && event.title.trim() ? event.title.trim() : '';
  const displayTitle = isCreationMode
    ? trimmedDraftTitle || savedEventTitle || 'Untitled Event'
    : trimmedDraftTitle || savedEventTitle || (loading ? 'Loading...' : 'Event');
  const progressPct = isCreationMode
    ? Math.round(((stepIndex + 1) / WIZARD_STEPS.length) * 100)
    : 0;
  const [originBack, setOriginBack] = useState<{ href: string; label: string } | null>(null);
  const chatUnread = useEventChatUnread(eventId);
  const inboxUnread = useOrganizerInboxUnread(eventId);
  const { entitlements: planEntitlements, hasFeature: engineHasFeature } = useEntitlements();

  useEffect(() => {
    setOriginBack(readEventOrigin());
  }, [eventId]);

  const backHref = originBack?.href ?? '/dashboard';
  const backLabel = originBack?.label ?? 'Back to Main Dashboard';

  return (
    <aside className="eh-sidebar flex w-72 h-screen overflow-hidden">
      <div className="eh-sidebar-header space-y-4 w-full">
        <Link href="/" className="block px-5 pt-6 pb-2" aria-label="EnthiosHub home">
          <SidebarBrandLogo />
        </Link>
      </div>

      <div
        className="eh-sidebar-scroll custom-scrollbar space-y-0.5"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {isCreationMode ? (
          <>
            <div className="mx-1 mb-3 space-y-2 px-2 py-1">
              <div className="flex items-center justify-between eh-sidebar-progress-label">
                <span>Step {stepIndex + 1} of {WIZARD_STEPS.length}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-bg/10">
                <div
                  className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
            <p className="eh-sidebar-section px-1 pb-2">
              Setup steps
            </p>
            <div className="space-y-1">
            {WIZARD_STEPS.map((step, idx) => {
              const active = idx === stepIndex;
              const done = idx < stepIndex;
              const label = CREATION_STEP_LABELS[idx] ?? step.title;
              const stepHref = `${editHref}?step=${step.id}`;
              return (
                <StaticDynamicLink
                  key={step.id}
                  href={stepHref}
                  onClick={(e) => {
                    e.preventDefault();
                    setStepIndex(idx);
                    if (typeof window !== 'undefined') {
                      const url = new URL(window.location.href);
                      url.searchParams.set('step', step.id);
                      window.history.pushState(null, '', url.pathname + url.search);
                    }
                  }}
                  className={`eh-sidebar-link ${
                    active
                      ? 'eh-sidebar-link--pill-active'
                      : done
                        ? 'text-[rgba(152,170,157,0.95)] hover:bg-bg/8'
                        : ''
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                      done
                        ? 'bg-[rgba(152,170,157,0.28)] text-[#d7e3d9]'
                        : active
                          ? 'bg-[var(--color-text)] text-text-inverse'
                          : 'bg-bg/10 text-text-inverse/85'
                    }`}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : idx + 1}
                  </span>
                  <span className="flex-1 leading-snug">{label}</span>
                </StaticDynamicLink>
              );
            })}
            </div>
            <div className="mt-6 px-4 pt-4 border-t border-white/5">
              {hasEventId ? (
                <StaticDynamicLink
                  href={`${base}/overview`}
                  className="eh-sidebar-muted font-medium hover:text-text-inverse transition-colors"
                >
                  Exit to overview {'->'}
                </StaticDynamicLink>
              ) : (
                <Link
                  href="/dashboard/events"
                  className="eh-sidebar-muted font-medium hover:text-text-inverse transition-colors"
                >
                  Exit to events {'->'}
                </Link>
              )}
            </div>
          </>
        ) : experienceLoading && navSections.length === 0 ? (
          <p className="eh-sidebar-muted px-4 py-6">Loading navigation…</p>
        ) : (
          (() => {
            const groups: { title: string; items: typeof navSections }[] = [
              { title: 'MAIN', items: [] },
              { title: 'REGISTRATIONS', items: [] },
              { title: 'COMMUNICATIONS', items: [] },
              { title: 'MANAGEMENT', items: [] },
              { title: 'REPORTS', items: [] },
              { title: 'SETTINGS', items: [] },
            ];
            
            const overviewItem = navSections.find((i) => i.slug === 'overview') ?? { slug: 'overview', label: 'Overview', icon: LayoutDashboard };
            const activitiesItem = navSections.find((i) => i.slug === 'activities') ?? { slug: 'activities', label: 'Activities', icon: Workflow };
            const participantsItem = navSections.find((i) => i.slug === 'participants') ?? { slug: 'participants', label: 'Participants', icon: Users };
            groups[0].items = [overviewItem, activitiesItem, participantsItem];
            
            navSections.forEach(item => {
              const slug = item.slug;
              if (['overview', 'activities', 'participants', 'program', 'visibility', 'evaluation', 'results', 'finance', 'financials', 'finance-settings'].includes(slug)) return;
              if (['attendance', 'forms'].includes(slug)) groups[1].items.push(item);
              else if (['communications'].includes(slug)) groups[2].items.push(item);
              else if (['team-management', 'evaluation-dashboard'].includes(slug)) groups[3].items.push(item);
              else if (['reports', 'analytics'].includes(slug)) groups[4].items.push(item);
              else if (['settings'].includes(slug)) groups[5].items.push(item);
              else groups[5].items.push(item);
            });
            
            return groups.filter(g => g.items.length > 0).map((group) => (
              <div key={group.title} className="mb-4">
                <div className="px-3 mb-1">
                  <p className="eh-sidebar-section">{group.title}</p>
                </div>
                {group.items.map((item) => {
                  const href = `${base}/${item.slug.replace(/^\/+/, '')}`;
                  const isActive = item.slug === 'activities'
                    ? Boolean(pathname?.includes(`/events/${eventId}/activities`)) || pathname === href
                    : pathname === href || pathname?.startsWith(href + '/');
                  const Icon = item.icon;
                  
                  let itemGroups = navGroupsByParent.get(item.slug) ?? [];
                  if (item.slug === 'communications') {
                    itemGroups = [
                      {
                        id: 'communications-tools',
                        parent_section: 'communications',
                        label: 'Tools',
                        visible: true,
                        items: [
                          { id: 'chat', label: 'Support Chat', route_slug: 'communications', route: '/communications', visible: true },
                          { id: 'email-campaigns', label: 'Email Campaigns', route_slug: 'email-campaigning', route: '/email-campaigning', visible: true }
                        ]
                      }
                    ];
                  }
      
                  const req = SLUG_FEATURE_MAP[item.slug];
                  const userPlanKey = (planEntitlements?.plan_key || 'silver').toLowerCase();
                  const userRank = PLAN_RANK[userPlanKey] ?? 2;
                  const reqRank = req ? (PLAN_RANK[req.requiredPlan.toLowerCase()] ?? 1) : 0;

                  const hasFeature = Boolean(
                    req &&
                    (engineHasFeature(req.feature) ||
                      req.aliases?.some((a) => engineHasFeature(a)) ||
                      (planEntitlements?.features && (
                        planEntitlements.features.includes(req.feature) ||
                        req.aliases?.some((a) => planEntitlements.features?.includes(a))
                      )))
                  );
                  const isLocked =
                    Boolean(req) &&
                    Boolean(planEntitlements) &&
                    userPlanKey !== 'custom' &&
                    userPlanKey !== 'enterprise' &&
                    !(hasFeature || userRank >= reqRank);

                  return (
                    <div key={item.slug} className="space-y-0.5">
                      <StaticDynamicLink
                        href={href}
                        onClick={(e) => {
                          if (isLocked) {
                            e.preventDefault();
                            triggerPlanUpgrade({
                              reason: 'FEATURE',
                              feature: req.feature,
                              currentPlan: userPlanKey,
                              requiredPlan: userRank >= reqRank ? (userRank < 2 ? 'silver' : 'custom') : req.requiredPlan,
                            });
                          }
                        }}
                        className={`eh-sidebar-link group ${
                          isActive ? 'eh-sidebar-link--active' : ''
                        } ${isLocked ? 'opacity-70 hover:opacity-100' : ''}`}
                      >
                        <Icon
                          className={`eh-sidebar-link-icon ${isActive ? '' : 'group-hover:text-text-inverse'}`}
                        />
                        <span className="flex-1">{item.label}</span>
                        {isLocked ? (
                          <Lock className="w-3 h-3 text-amber-400/80 mr-1" />
                        ) : null}
                        {item.slug === 'chat' && chatUnread > 0 ? (
                          <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-text-inverse">
                            {chatUnread > 99 ? '99+' : chatUnread}
                          </span>
                        ) : null}
                        {(item.slug === 'inbox' || item.slug === 'communications') && inboxUnread > 0 ? (
                          <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-dark">
                            {inboxUnread > 99 ? '99+' : inboxUnread}
                          </span>
                        ) : null}
                        {isActive && <ChevronRight className="w-3 h-3 text-text-inverse/50" />}
                      </StaticDynamicLink>
                      {itemGroups.length > 0 && !isMunWorkspace
                        ? itemGroups.map((group) => (
                            <div key={group.id} className="ml-3 border-l border-white/10 pl-2 space-y-0.5">
                              <p className="eh-sidebar-subsection">
                                {group.label}
                              </p>
                              {group.items.map((sub, subIndex) => {
                                const subHref = `${base}/${sub.route_slug}`;
                                const subActive = pathname === subHref || pathname?.startsWith(subHref + '/');
                                const subKey = `${group.id}-${sub.route_slug ?? sub.id ?? subIndex}`;
                                const unread =
                                  sub.route_slug === 'chat'
                                    ? chatUnread
                                    : sub.route_slug === 'inbox' || sub.route_slug === 'communications'
                                      ? inboxUnread
                                      : 0;
                                return (
                                  <StaticDynamicLink
                                    key={subKey}
                                    href={subHref}
                                    className={`eh-sidebar-sublink ${
                                      subActive ? 'eh-sidebar-sublink--active' : ''
                                    }`}
                                  >
                                    <span className="flex-1">{sub.label}</span>
                                    {unread > 0 ? (
                                      <span
                                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                                          sub.route_slug === 'chat'
                                            ? 'bg-red-500 text-text-inverse'
                                            : 'bg-amber-400 text-dark'
                                        }`}
                                      >
                                        {unread > 99 ? '99+' : unread}
                                      </span>
                                    ) : null}
                                  </StaticDynamicLink>
                                );
                              })}
                            </div>
                          ))
                        : null}
                    </div>
                  );
                })}
              </div>
            ))
          })()
        )}
      </div>

      <div className="eh-sidebar-footer">
        <div className="eh-sidebar-footer-badge">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>
            {isCreationMode ? 'Creation Mode' : 'Active Workspace'}
          </span>
        </div>
      </div>
    </aside>
  );
}

export const EventWorkspaceSidebar = React.memo(EventWorkspaceSidebarComponent);
